import { useCallback, useMemo, useState } from "react";

import { type ViewColumnDef } from "@moment/api-collab/api-types";
import { type JsonPathLiteral } from "@moment/api-collab/differential";
import { useCellServices } from "@moment/design-system/CellServicesContext";

import { type BaseViewHandle } from "./BaseViewHandle";
import { chooseNewColumnHeader, existingAccessorKeys, headerToAccessorKey } from "./columnUtil";
import { useBaseViewQuery } from "./useBaseViewQuery";
import { useHydratedColumnDefs } from "./useHydratedColumnDefs";
import { type HydratedRow, useHydratedRows } from "./useHydratedRows";

export const useBaseView = (): BaseViewHandle => {
	const { cellId: viewId, documentInteractionMode, base } = useCellServices();
	const { viewSpec, results: data } = useBaseViewQuery(viewId);
	const table = viewSpec?.query.spec[0].table;
	const disabled = documentInteractionMode === "static";

	const hydratedColumnDefs = useHydratedColumnDefs(viewSpec);
	const hydratedRows = useHydratedRows(data, viewSpec?.query, hydratedColumnDefs, base, disabled);

	const [openItemIndex, setOpenItemIndex] = useState<number | undefined>(undefined);
	const openItem = openItemIndex !== undefined ? hydratedRows[openItemIndex] : undefined;

	const closeRowDialog = useCallback(() => setOpenItemIndex(undefined), []);

	const updateViewTitle = useCallback(
		(title: string) => {
			if (!viewSpec) {
				return;
			}
			void base?.updateView(viewId, { ...viewSpec, viewTitle: title });
		},
		[viewSpec, base, viewId]
	);

	const renameColumn = useCallback(
		(
			oldColumnAccessorKey: JsonPathLiteral,
			newHeader: string,
			newAccessorKey: JsonPathLiteral
		) => {
			void base?.renameColumn(viewId, oldColumnAccessorKey, newHeader, newAccessorKey);
		},
		[base, viewId]
	);

	const updateColumn = useCallback(
		(accessorKey: string, column: ViewColumnDef) => {
			console.log("UPDATE COLUMN 1", accessorKey, column);
			if (!viewSpec) {
				return;
			}

			// FIXME: There has got to be a better way to do this.
			const index = viewSpec.columns.findIndex((c) => c.accessorKey === accessorKey);
			console.log("UPDATE COLUMN 2", accessorKey, column, index);
			if (index === -1) {
				throw new Error(`Column with accessor key ${accessorKey} not found`);
			} else {
				const newColumns = [...viewSpec.columns];
				newColumns[index] = column;
				if (column.primaryDisplayColumn) {
					const otherPrimaryDisplayColumn = newColumns.find(
						(c) => c.primaryDisplayColumn && c.accessorKey !== column.accessorKey
					);
					if (otherPrimaryDisplayColumn) {
						otherPrimaryDisplayColumn.primaryDisplayColumn = false;
					}
				}

				void base?.updateView(viewId, { ...viewSpec, columns: newColumns });
			}
		},
		[viewSpec, base, viewId]
	);

	const appendColumn = useCallback(async () => {
		if (!viewSpec) {
			return;
		}

		const columnName = chooseNewColumnHeader(hydratedColumnDefs);
		const accessorKey = headerToAccessorKey(
			columnName,
			existingAccessorKeys(hydratedColumnDefs)
		);
		const column: ViewColumnDef = {
			accessorKey: `$.${accessorKey}`,
			header: columnName,
			type: "Text",
		};
		await base?.appendColumn(viewId, column);
	}, [viewSpec, hydratedColumnDefs, base, viewId]);

	const deleteColumn = useCallback(
		async (column: ViewColumnDef) => {
			await base?.deleteColumn(viewId, column);
		},
		[base, viewId]
	);

	const appendRow = useCallback(() => {
		if (!table) {
			throw new Error("Table not found");
		}
		const id = crypto.randomUUID();
		void base?.insert(table, id, { id });
		return id;
	}, [base, table]);

	const deleteRow = useCallback(
		(row: HydratedRow) => {
			if (!table) {
				throw new Error("Table not found");
			}
			// FIXME: We need a notion of a primary key, lol.
			const recordId: string | undefined = row.original?.["id"];
			if (!recordId) {
				throw new Error("Primary key not found");
			}
			void base?.delete(table, recordId);
		},
		[base, table]
	);

	const openRowDialogAtPrevious = useCallback(() => {
		if (openItemIndex === undefined || openItemIndex === 0) {
			return;
		}
		setOpenItemIndex(openItemIndex - 1);
	}, [openItemIndex]);

	const openRowDialogAtNext = useCallback(() => {
		if (openItemIndex === undefined || openItemIndex === hydratedRows.length - 1) {
			return;
		}
		setOpenItemIndex(openItemIndex + 1);
	}, [openItemIndex, hydratedRows]);

	return useMemo(
		() =>
			({
				spec: viewSpec,
				table,
				data: hydratedRows,
				columnDefs: hydratedColumnDefs,
				updateViewTitle,
				updateColumn,
				renameColumn,
				appendColumn,
				deleteColumn,
				appendRow,
				deleteRow,
				rowDialogOpen: openItemIndex !== undefined,
				openItemIndex,
				openItem,
				openRowDialogAtPrevious,
				openRowDialogAtNext,
				openRowDialogAt: setOpenItemIndex,
				closeRowDialog,
			}) satisfies BaseViewHandle,
		[
			viewSpec,
			table,
			hydratedRows,
			hydratedColumnDefs,
			updateViewTitle,
			updateColumn,
			renameColumn,
			appendColumn,
			deleteColumn,
			appendRow,
			deleteRow,
			openItemIndex,
			openItem,
			openRowDialogAtPrevious,
			openRowDialogAtNext,
			closeRowDialog,
		]
	);
};
